import { useState } from "react";
import { useNavigate } from "react-router";
import { ClipboardList, DollarSign, FileText, Handshake, Link2, MoreHorizontal, RotateCcw, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useFeature } from "@/lib/use-feature";
import { usePermissao } from "@/lib/use-permissao";
import { PartnerConnectDialog } from "./PartnerConnectDialog";

type Cadastro = {
  id: string;
  type: string;
  name: string;
  phone: string | null;
  email: string | null;
  status: "ACTIVE" | "INACTIVE";
  linkedOrganizationId: string | null;
  isPartner: boolean;
};

const CLIENTES = new Set(["DENTIST", "CLINIC_NETWORK"]);
const item = "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-zinc-700 outline-none data-[highlighted]:bg-zinc-100 dark:text-foreground dark:data-[highlighted]:bg-muted";

/** Os três pontinhos da linha em Cadastros: atalhos para as abas do perfil e as ações de sempre
 *  (parceria, convite, ativar/desativar) sem abrir a página. */
export function AcoesDoCadastro({ cadastro: c }: { cadastro: Cadastro }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [convidando, setConvidando] = useState(false);
  const ehDono = useAuth().user?.activeOrganization?.role === "OWNER";
  const temFinanceiro = useFeature("financeiro");
  const veFinanceiro = usePermissao("finance.view");
  const vePreco = usePermissao("prices.view");
  const clienteConectado = CLIENTES.has(c.type) && !!c.linkedOrganizationId;

  const invalidar = () => {
    utils.partner.list.invalidate();
    utils.partner.countByType.invalidate();
    utils.partner.getById.invalidate({ id: c.id });
    utils.labPricing.clientPricing.listPartners.invalidate();
  };
  const aoMudar = (msg: string) => ({ onSuccess: () => (toast.success(msg), invalidar()), onError: (e: { message: string }) => toast.error(e.message) });
  const desativar = trpc.partner.disable.useMutation(aoMudar("Cadastro desativado."));
  const reativar = trpc.partner.activate.useMutation(aoMudar("Cadastro reativado."));
  const parceria = trpc.partner.setIsPartner.useMutation(aoMudar("Parceria atualizada."));
  const ir = (aba: string) => navigate(`/lab/cadastros/${c.id}?aba=${aba}`);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Ações de ${c.name}`}
            className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted dark:hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="z-[60] min-w-[220px] border border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card">
          <DropdownMenuItem className={item} onSelect={() => ir("dados")}>
            <UserRound className="h-4 w-4" />
            Abrir cadastro
          </DropdownMenuItem>
          {clienteConectado && (
            <DropdownMenuItem className={item} onSelect={() => ir("pedidos")}>
              <ClipboardList className="h-4 w-4" />
              Pedidos
            </DropdownMenuItem>
          )}
          {clienteConectado && temFinanceiro && veFinanceiro && (
            <DropdownMenuItem className={item} onSelect={() => ir("extrato")}>
              <FileText className="h-4 w-4" />
              Extrato
            </DropdownMenuItem>
          )}
          {clienteConectado && vePreco && (
            <DropdownMenuItem className={item} onSelect={() => ir("precos")}>
              <DollarSign className="h-4 w-4" />
              Preços
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className="my-1 h-px bg-zinc-100 dark:bg-border" />
          {clienteConectado && ehDono && (
            <DropdownMenuItem className={item} onSelect={() => parceria.mutate({ id: c.id, isPartner: !c.isPartner })}>
              <Handshake className="h-4 w-4" />
              {c.isPartner ? "Tirar a parceria" : "Marcar como clínica parceira"}
            </DropdownMenuItem>
          )}
          {c.type === "DENTIST" && !c.linkedOrganizationId && (
            <DropdownMenuItem className={item} onSelect={() => setConvidando(true)}>
              <Link2 className="h-4 w-4" />
              Convidar pra plataforma
            </DropdownMenuItem>
          )}
          {c.status === "ACTIVE" ? (
            <DropdownMenuItem
              className={`${item} text-red-600 dark:text-red-400`}
              onSelect={() => confirm(`Desativar o cadastro de ${c.name}?`) && desativar.mutate({ id: c.id })}
            >
              <Trash2 className="h-4 w-4" />
              Desativar
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem className={`${item} text-emerald-700 dark:text-emerald-400`} onSelect={() => reativar.mutate({ id: c.id })}>
              <RotateCcw className="h-4 w-4" />
              Ativar
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <PartnerConnectDialog
        partner={convidando ? { id: c.id, name: c.name, phone: c.phone, email: c.email } : null}
        onClose={() => setConvidando(false)}
      />
    </>
  );
}
